import { Button, Dialog, DotsLoader } from '@eppendorf/vnls-react-components';
import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { getLocalizedDateFromUtcString } from '$shared/utils/date.utils';

import './organization-devices-logs.scss';
import { getDeviceLogs, useGetAvailableDeviceLogs, useGetOrganization, useRequestLogsUpload } from '$features/organizations';

import { SIDECAR_ROUTE_PARAMS } from '../../../../sidecar-route-params';

export function OrganizationDevicesLogs(): ReactElement {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState('');
  const [submitSerialNumber, setSubmitSerialNumber] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [search] = useSearchParams();
  const isValidSerialNumber = useMemo(() => submitSerialNumber.length === 12, [submitSerialNumber]);

  const { data: organization } = useGetOrganization(search.get(SIDECAR_ROUTE_PARAMS.organization.id) || '') as {
    data?: { id?: string };
  };

  const { data: availableDeviceLogs, isLoading: isLoadingAvailableDeviceLogs } = useGetAvailableDeviceLogs(organization?.id || '') as {
    data?: { files?: Array<{ fileName: string; creationDate: string }> };
    isLoading: boolean;
  };

  const { mutateAsync: requestLogsUpload, isPending: isLoadingRequestLogsUpload, isError, isSuccess, error } = useRequestLogsUpload();

  const handleSubmit = async () => {
    await requestLogsUpload({
      tenantId: organization?.id || '',
      serialNumber: submitSerialNumber,
    });
    setIsDialogOpen(false);
  };

  const downloadLogs = async (fileName: string) => {
    const presignedUrl = await getDeviceLogs(organization?.id || '', fileName);
    window.open(presignedUrl.presignedUrl, '_blank');
  };

  useEffect(() => {
    if (!isSuccess && !isError) {
      return;
    }

    // @ts-expect-error - error is defined in the response
    const message = isError ? error.response?.data?.message || error?.message : 'Request submitted successfully!';
    toast(message, { type: isError ? 'error' : 'success' });
  }, [isSuccess, isError, error]);

  return (
    <>
      <div className="flex flex__dir--row flex__ai--center flex__jc--space-between w-full">
        <input type="text" className="input w-m" style={{ height: '1rem' }} placeholder="Search" onChange={(e) => setSearchTerm(e.target.value)} />
        <Button variant="primary" className="m-left-m" onClick={() => setIsDialogOpen(true)}>
          {t('organizationDetails.requestLogs')}
        </Button>
      </div>
      <div className="h-5xl overflow-y-scroll">
        <table className="organization-logs-table">
          <thead>
            <tr>
              <th className="w-xl">{t('organizationDetails.fileName')}</th>
              <th className="w-m">{t('organizationDetails.createdAt')}</th>
            </tr>
          </thead>
          <tbody>
            {!isLoadingAvailableDeviceLogs &&
              (availableDeviceLogs?.files?.length ? (
                availableDeviceLogs?.files
                  .filter(({ fileName }) => fileName.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map((logFile) => (
                    <tr key={logFile.fileName}>
                      <td>
                        <button type="button" onClick={() => downloadLogs(logFile.fileName)} className="btn-link">
                          <span className="color-blue-500">{logFile.fileName}</span>
                        </button>
                      </td>
                      <td>
                        <span className="color-gray-500">{getLocalizedDateFromUtcString(logFile.creationDate)}</span>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan={2}>
                    <div className="flex flex__dir--column">
                      <p className="font-weight-medium font-size-l">{t('organizationDetails.noLogsFound')}</p>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {isLoadingAvailableDeviceLogs && <DotsLoader className="m-top-xl" />}
      </div>

      <Dialog isOpen={isDialogOpen} onOpenChange={(open) => setIsDialogOpen(open)} className="w-max-4xl">
        <Dialog.Title>{t('organizationDetails.enterSerialNumber')}</Dialog.Title>
        <Dialog.Description className="m-y-4xl">
          <div className="flex flex__dir--column flex__ai--center">
            <div className="flex flex__dir--column w-full">
              <input
                type="text"
                className="input w-full"
                style={{ height: '48rem' }}
                placeholder="i.e ESS1XXX3XXXX"
                onChange={(e) => setSubmitSerialNumber(e.target.value)}
                value={submitSerialNumber}
              />
              {submitSerialNumber && !isValidSerialNumber && <span className="color-red-500">{t('organizationDetails.invalidSerialNumber')}</span>}
            </div>
          </div>
        </Dialog.Description>
        <Dialog.Actions>
          <Dialog.Close>
            <Button variant="secondary">{t('shared.cancel')}</Button>
          </Dialog.Close>
          <Button variant="primary" onClick={handleSubmit} disabled={!isValidSerialNumber || isLoadingRequestLogsUpload}>
            {isLoadingRequestLogsUpload ? t('organizationDetails.requesting') : t('shared.submit')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </>
  );
}
